import axios from 'axios'
import { readCsrfToken, syncCsrfTokenMeta } from '../../shared/csrf.js'

const client = axios.create({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

client.interceptors.request.use((config) => {
  const token = readCsrfToken()
  if (token) {
    config.headers['X-CSRF-TOKEN'] = token
  }

  return config
})

client.interceptors.response.use(
  (response) => {
    syncCsrfTokenMeta()
    return response
  },
  async (error) => {
    const { config, response } = error

    if (response?.status === 419 && config && !config.__csrfRetried) {
      syncCsrfTokenMeta()
      config.__csrfRetried = true
      config.headers['X-CSRF-TOKEN'] = readCsrfToken()

      return client.request(config)
    }

    if (response?.status === 401) {
      error.sessionExpired = true
    }

    return Promise.reject(error)
  },
)

export const registerApi = {
  registerPatient: (payload) => client.post('/register-api/patient', payload),

  registerPractice: (payload) => client.post('/register-api/practice', payload),

  getPracticeStatus: () => client.get('/register-api/practice/status'),

  getPatientStatus: () => client.get('/register-api/patient/status'),

  lookupReferral: (code) => client.get(`/register-api/referral/${encodeURIComponent(code)}`),

  validateEmail: (email) => client.post('/register-api/validate/email', { email }),

  validatePhone: (phone_number) => client.post('/register-api/validate/phone', { phone_number }),

  resendVerificationEmail: () => client.post('/register-api/practice/verify/resend'),

  getVerificationStatus: () => client.get('/register-api/practice/verify/status'),

  updateVerifyMethod: (type) => client.put('/register-api/practice/verify/method', { type }),

  sendPhoneVerificationCode: () => client.post('/register-api/practice/verify/phone/send'),

  verifyPhoneCode: (code) => client.post('/register-api/practice/verify/phone', { code }),

  resendPatientVerificationEmail: () => client.post('/register-api/patient/verify/resend'),

  getPatientVerificationStatus: () => client.get('/register-api/patient/verify/status'),

  updatePatientVerifyMethod: (type) => client.put('/register-api/patient/verify/method', { type }),

  sendPatientPhoneVerificationCode: () => client.post('/register-api/patient/verify/phone/send'),

  verifyPatientPhoneCode: (code) => client.post('/register-api/patient/verify/phone', { code }),

  submitManager: (payload) => client.post('/register-api/practice/manager', payload),

  skipManager: () => client.post('/register-api/practice/manager/skip'),

  lookupNpi: (number) => client.get('/register-api/npi/lookup', { params: { number } }),
}

export function extractRegisterError(error, field, fallback = 'Something went wrong. Please try again.') {
  if (error.response?.status === 429) {
    return error.response?.data?.message ?? 'Too many attempts. Please try again later.'
  }

  if (error.response?.status === 401 || error.sessionExpired) {
    return 'Your session has expired. Please refresh the page and sign in again to continue registration.'
  }

  if (error.response?.status === 403) {
    return error.response?.data?.message ?? 'You are not authorized to continue registration. Please refresh and try again.'
  }

  if (error.response?.status === 409) {
    const loginUrl = error.response?.data?.login_url
    const baseMessage = error.response?.data?.message ?? 'Account with this credential already exists.'
    return loginUrl ? `${baseMessage}` : baseMessage
  }

  const message = error.response?.data?.message
  if (message && !error.response?.data?.errors) {
    return message
  }

  const errors = error.response?.data?.errors
  if (!errors) return fallback
  const messages = errors[field] ?? Object.values(errors)[0]
  return Array.isArray(messages) ? messages[0] : fallback
}

export function extractFieldErrors(error) {
  const errors = error.response?.data?.errors
  if (!errors) return {}

  return Object.fromEntries(
    Object.entries(errors).map(([key, messages]) => [
      key,
      Array.isArray(messages) ? messages[0] : String(messages),
    ]),
  )
}
